import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { BalanceIndex } from '../money.model';
import { formatCents } from '../format';
import { todayISO } from '../input';
import { LinePoint } from '../charts/line-chart.component';
import {
  AccountSummary, BalanceMonth, BALANCE_MONTHS, accountSummaries, monthEndBalances,
} from './accounts.calc';

@Component({
  selector: 'app-money-accounts',
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.scss'],
  standalone: false,
})
export class AccountsComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';
  summaries: AccountSummary[] = [];

  protected balances: BalanceIndex = {};
  private subs: Subscription[] = [];

  readonly fmt = formatCents;

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.subs.push(this.money.subscribeAll(user.uid).subscribe({
        next: data => {
          this.balances = data.balances;
          this.summaries = accountSummaries(
            data.accounts, data.txns, data.balances, todayISO());
          this.loading = false;
        },
        error: (e: unknown) => {
          this.error = `Could not load your accounts: ${(e as Error).message}. `
            + 'Try reloading the page.';
          this.loading = false;
        },
      }));
    }));
  }

  // BOTH subscriptions: an unstored authState subscription outlives the
  // component and re-runs against a destroyed instance on the next auth event.
  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  trackAccount(_: number, s: AccountSummary): string { return s.id; }

  /** Closing balance per month for one account; empty when it reports no balances. */
  balanceSeries(accountId: string): BalanceMonth[] {
    return monthEndBalances(this.balances[accountId], BALANCE_MONTHS);
  }

  /** Chart points. Charts never format money — displayValues carries the text. */
  balancePoints(accountId: string): LinePoint[] {
    return this.balanceSeries(accountId)
      .map(m => ({ label: this.monthShort(m.month), value: m.cents }));
  }

  balanceDisplay(accountId: string): string[] {
    return this.balanceSeries(accountId).map(m => formatCents(m.cents));
  }

  /**
   * 'Aug 26' from '2026-08'. The year is not decoration: this series runs 24
   * months, so bare month names would repeat, and these labels are what the
   * chart's aria-labels and hidden data table read out.
   *
   * Appending a day keeps Date off the timezone boundary — a bare ISO date is
   * parsed as UTC midnight and lands on the previous day west of Greenwich.
   */
  monthShort(month: string): string {
    return new Date(`${month}-01T00:00:00`)
      .toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
}
